(function () {
  const ROOT_SELECTOR = '[data-before-after-root]';
  const OPTION_SELECTOR = '[data-before-after-option]';
  const DRAG_THRESHOLD_PX = 6;
  const EDGE_GUARD_PX = 12;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function parseNumber(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getPositionFromClientX(compareElement, clientX) {
    if (!compareElement) return 50;

    const rect = compareElement.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 50;

    const guard = Math.min(EDGE_GUARD_PX, rect.width / 2);
    const minX = rect.left + guard;
    const maxX = rect.right - guard;
    const guardedX = clamp(clientX, minX, maxX);
    const ratio = (guardedX - rect.left) / rect.width;

    return clamp(ratio * 100, 0, 100);
  }

  function setPosition(root, value) {
    const compare = root.querySelector('[data-before-after-compare]');
    const range = root.querySelector('[data-before-after-range]');
    if (!compare || !range) return;

    const position = clamp(parseNumber(value, 50), 0, 100);
    compare.style.setProperty('--before-after-position', `${position}%`);
    range.value = Math.round(position);
    range.setAttribute('aria-valuenow', `${Math.round(position)}`);
  }

  function hydrateImages(root) {
    root.querySelectorAll('template[data-before-after-image]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  function updateImage(imageElement, url, altText, srcset, sizes) {
    if (!imageElement) return;

    // Keep responsive candidates when options change; set them before src.
    if (srcset) {
      imageElement.setAttribute('sizes', sizes || 'auto, 100vw');
      imageElement.setAttribute('srcset', srcset);
    } else {
      imageElement.removeAttribute('srcset');
      imageElement.removeAttribute('sizes');
    }
    if (url) imageElement.setAttribute('src', url);

    if (typeof altText === 'string' && altText.length) {
      imageElement.setAttribute('alt', altText);
    }
  }

  function activateOption(root, option, shouldFocus) {
    if (!option) return;

    hydrateImages(root);

    const beforeImage = root.querySelector('[data-before-image]');
    const afterImage = root.querySelector('[data-after-image]');
    const beforeLabel = root.querySelector('[data-before-after-label="before"]');
    const afterLabel = root.querySelector('[data-before-after-label="after"]');

    root.querySelectorAll(OPTION_SELECTOR).forEach((button) => {
      const isActive = button === option;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const beforeUrl = option.dataset.beforeUrl || root.dataset.defaultBeforeUrl;
    const afterUrl = option.dataset.afterUrl || root.dataset.defaultAfterUrl;
    const beforeAlt = option.dataset.beforeAlt || option.dataset.beforeLabel || root.dataset.defaultBeforeLabel;
    const afterAlt = option.dataset.afterAlt || option.dataset.afterLabel || root.dataset.defaultAfterLabel;
    const nextBeforeLabel = option.dataset.beforeLabel || root.dataset.defaultBeforeLabel;
    const nextAfterLabel = option.dataset.afterLabel || root.dataset.defaultAfterLabel;

    updateImage(beforeImage, beforeUrl, beforeAlt, option.dataset.beforeSrcset || root.dataset.defaultBeforeSrcset, root.dataset.responsiveSizes);
    updateImage(afterImage, afterUrl, afterAlt, option.dataset.afterSrcset || root.dataset.defaultAfterSrcset, root.dataset.responsiveSizes);

    if (beforeLabel && nextBeforeLabel) beforeLabel.textContent = nextBeforeLabel;
    if (afterLabel && nextAfterLabel) afterLabel.textContent = nextAfterLabel;

    setPosition(root, option.dataset.position || root.dataset.defaultPosition || 50);

    if (shouldFocus) option.focus();
  }

  function bindDrag(root, range, compare) {
    if (!root || !range || !compare) return;
    if (root.dataset.beforeAfterDragBound === 'true') return;
    root.dataset.beforeAfterDragBound = 'true';

    let activePointerId = null;
    let activeTouchId = null;
    let dragState = null;
    let isHorizontalDrag = false;

    function startDrag(clientX, clientY) {
      dragState = {
        startX: clientX,
        startY: clientY,
      };
      isHorizontalDrag = false;
    }

    function enableHorizontalDragMode() {
      if (isHorizontalDrag) return;
      isHorizontalDrag = true;
      compare.style.touchAction = 'none';
    }

    function clearDrag() {
      activePointerId = null;
      activeTouchId = null;
      dragState = null;
      isHorizontalDrag = false;
      compare.style.removeProperty('touch-action');
    }

    function moveDrag(clientX, clientY, event) {
      if (!dragState) return;

      if (!isHorizontalDrag) {
        const deltaX = Math.abs(clientX - dragState.startX);
        const deltaY = Math.abs(clientY - dragState.startY);
        if (deltaX < DRAG_THRESHOLD_PX && deltaY < DRAG_THRESHOLD_PX) return;

        if (deltaY > deltaX) {
          clearDrag();
          return;
        }

        enableHorizontalDragMode();
      }

      const nextPosition = getPositionFromClientX(compare, clientX);
      setPosition(root, nextPosition);
      if (event && event.cancelable) event.preventDefault();
    }

    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      activePointerId = event.pointerId;
      startDrag(event.clientX, event.clientY);

      if (compare.setPointerCapture) {
        try {
          compare.setPointerCapture(activePointerId);
        } catch (error) {
          // Ignore pointer capture failures.
        }
      }
    }

    function onPointerMove(event) {
      if (activePointerId === null || event.pointerId !== activePointerId) return;
      moveDrag(event.clientX, event.clientY, event);
    }

    function onPointerUp(event) {
      if (activePointerId === null || event.pointerId !== activePointerId) return;
      clearDrag();
    }

    function getTouchById(event, touchId) {
      return Array.from(event.changedTouches || []).find((touch) => touch.identifier === touchId);
    }

    function onTouchStart(event) {
      if (activeTouchId !== null || !event.changedTouches || !event.changedTouches.length) return;

      const touch = event.changedTouches[0];
      activeTouchId = touch.identifier;
      startDrag(touch.clientX, touch.clientY);
    }

    function onTouchMove(event) {
      if (activeTouchId === null) return;
      const touch = getTouchById(event, activeTouchId);
      if (!touch) return;
      moveDrag(touch.clientX, touch.clientY, event);
    }

    function onTouchEnd(event) {
      if (activeTouchId === null) return;
      const touch = getTouchById(event, activeTouchId);
      if (!touch) return;
      clearDrag();
    }

    if (window.PointerEvent) {
      compare.addEventListener('pointerdown', onPointerDown);
      compare.addEventListener('pointermove', onPointerMove);
      compare.addEventListener('pointerup', onPointerUp);
      compare.addEventListener('pointercancel', onPointerUp);
      compare.addEventListener('lostpointercapture', onPointerUp);
    } else {
      compare.addEventListener('touchstart', onTouchStart, { passive: true });
      compare.addEventListener('touchmove', onTouchMove, { passive: false });
      compare.addEventListener('touchend', onTouchEnd);
      compare.addEventListener('touchcancel', onTouchEnd);
    }
  }

  function bindRoot(root) {
    if (!root || root.dataset.beforeAfterBound === 'true') return;
    root.dataset.beforeAfterBound = 'true';

    const range = root.querySelector('[data-before-after-range]');
    const compare = root.querySelector('[data-before-after-compare]');
    if (!range) return;

    range.addEventListener('input', () => {
      setPosition(root, range.value);
    });

    setPosition(root, range.value || root.dataset.defaultPosition || 50);

    const options = Array.from(root.querySelectorAll(OPTION_SELECTOR));
    options.forEach((button) => {
      button.addEventListener('click', () => activateOption(root, button, false));
    });

    // Keep off-screen image requests out of the initial loading window.
    // Explicit interaction always loads the currently selected pair immediately.
    let imageObserver = null;
    function prepareImages() {
      if (!root.querySelector('template[data-before-after-image]')) return;
      hydrateImages(root);
      if (imageObserver) imageObserver.disconnect();
      const selected = options.find((button) => button.classList.contains('is-active'));
      if (selected) activateOption(root, selected, false);
    }
    if (root.querySelector('template[data-before-after-image]')) {
      root.addEventListener('focusin', prepareImages, { once: true });
      root.addEventListener('pointerdown', prepareImages, { once: true, passive: true });
      if ('IntersectionObserver' in window) {
        imageObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            prepareImages();
            imageObserver.disconnect();
          }
        }, { rootMargin: '450px 0px' });
        imageObserver.observe(compare || root);
      } else {
        prepareImages();
      }
    } else {
      const activeButton = options.find((button) => button.classList.contains('is-active'));
      if (activeButton) activateOption(root, activeButton, false);
    }

    if (compare) {
      bindDrag(root, range, compare);
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    const rootFromEvent = event.target && event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
    const buttonFromEventRoot = rootFromEvent
      ? rootFromEvent.querySelector(`${OPTION_SELECTOR}[data-block-id="${blockId}"]`)
      : null;
    const buttonFromDocument = document.querySelector(`${ROOT_SELECTOR} ${OPTION_SELECTOR}[data-block-id="${blockId}"]`);
    const button = buttonFromEventRoot || buttonFromDocument;
    if (!button) return;

    const root = button.closest(ROOT_SELECTOR);
    if (!root) return;

    activateOption(root, button, false);
    button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });
})();
